// Origin health (doc 03 §3.8) — the "feel the burn" view that makes the
// CDN-offload / restreamer decision data-driven rather than a feeling.
import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { api, type OriginLoad } from "../api";
import { Card, Empty, ErrorNote, PageHeader, Spinner, Stat, StatusDot, formatTime } from "../ui";

import { BURN_COLOR, CHART } from "../chartTheme";

const AXIS = CHART.axis;
const GRID = CHART.grid;
const SURFACE = CHART.surface;
const INK = CHART.ink;
const INK_BODY = CHART.inkBody;

export default function OriginHealth() {
  const { data, isLoading, error } = useQuery<OriginLoad>({
    queryKey: ["origin-load"],
    queryFn: () => api.get("/v1/reports/origin-load"),
    refetchInterval: 60_000,
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;
  if (!data) return null;

  const chartData = data.dvrs.map((dvr) => ({
    host: dvr.dvr_host.split(".")[0],
    fullHost: dvr.dvr_host,
    mbps: dvr.reported_mbps,
    sessions: dvr.reported_sessions,
    burn: dvr.burn,
  }));

  const worstBurn = data.dvrs.reduce<"ok" | "warn" | "critical">(
    (worst, dvr) => (dvr.burn === "critical" ? "critical" : dvr.burn === "warn" && worst !== "critical" ? "warn" : worst),
    "ok",
  );

  return (
    <>
      <PageHeader
        title="Origin health"
        subtitle="Distribution load attributed to each DVR, from the auth callback and from what the Outposts report pulling."
      />

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Distribution sessions" value={data.total_sessions} hint="HLS pulls across the fleet" />
        <Stat
          label="Distribution egress"
          value={`${data.total_mbps} Mbps`}
          tone={worstBurn === "critical" ? "crit" : worstBurn === "warn" ? "warn" : "ok"}
        />
        <Stat label="DVRs carrying load" value={data.dvrs.length} />
        <Stat
          label="Denied authorizations (2 h)"
          value={Object.values(data.denies_2h).reduce((a, b) => a + b, 0)}
          hint={Object.keys(data.denies_2h)[0] ?? "none"}
        />
      </div>

      <div
        className={
          worstBurn === "critical"
            ? "mb-6 rounded-md border border-state-crit/40 bg-state-crit/10 p-4 text-sm text-state-crit"
            : worstBurn === "warn"
              ? "mb-6 rounded-md border border-state-warn/40 bg-state-warn/10 p-4 text-sm text-state-warn"
              : "mb-6 rounded-md border border-ink-600 bg-ink-800 p-4 text-sm text-ink-300"
        }
      >
        <div className="mb-1 text-xs font-medium uppercase tracking-wider opacity-80">Recommendation</div>
        {data.recommendation}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="Distribution egress per DVR" padded={false}>
          {chartData.length === 0 ? (
            <Empty>No distribution load yet.</Empty>
          ) : (
            <figure className="p-4">
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 16, right: 16, bottom: 4, left: 4 }}>
                    <CartesianGrid stroke={GRID} vertical={false} />
                    <XAxis
                      dataKey="host"
                      tick={{ fill: AXIS, fontSize: 12 }}
                      axisLine={{ stroke: GRID }}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fill: AXIS, fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                      width={48}
                      label={{
                        value: "Mbps",
                        angle: -90,
                        position: "insideLeft",
                        fill: AXIS,
                        fontSize: 11,
                      }}
                    />
                    <Tooltip
                      cursor={{ fill: "#ffffff08" }}
                      contentStyle={{
                        background: SURFACE,
                        border: `1px solid ${GRID}`,
                        borderRadius: 6,
                        fontSize: 12,
                        color: INK,
                      }}
                      formatter={(value: number, _name, entry) => [
                        `${value} Mbps · ${entry.payload.sessions} sessions`,
                        entry.payload.fullHost,
                      ]}
                      labelFormatter={() => ""}
                    />
                    <Bar dataKey="mbps" radius={[4, 4, 0, 0]} maxBarSize={48}>
                      {chartData.map((entry) => (
                        <Cell key={entry.host} fill={BURN_COLOR[entry.burn]} />
                      ))}
                      <LabelList
                        dataKey="mbps"
                        position="top"
                        fill={INK_BODY}
                        fontSize={11}
                        formatter={(v: number) => (v > 0 ? v : "")}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <figcaption className="mt-3 flex flex-wrap gap-4 text-xs text-ink-400">
                <LegendSwatch color={BURN_COLOR.ok} label={`inside headroom (< ${data.thresholds.warn_mbps} Mbps)`} />
                <LegendSwatch color={BURN_COLOR.warn} label="feeling the burn — plan the CDN offload" />
                <LegendSwatch color={BURN_COLOR.critical} label={`critical (>= ${data.thresholds.critical_mbps} Mbps)`} />
              </figcaption>
            </figure>
          )}
        </Card>

        <Card title="Per-DVR detail" padded={false}>
          {data.dvrs.length === 0 ? (
            <Empty>No DVRs reporting distribution load.</Empty>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>DVR</th>
                    <th>Sessions</th>
                    <th>Egress</th>
                    <th>On backup</th>
                    <th>Authorised</th>
                    <th>Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {data.dvrs.map((dvr) => (
                    <tr key={dvr.dvr_host}>
                      <td className="font-mono text-xs">{dvr.dvr_host}</td>
                      <td className="tabular-nums">{dvr.reported_sessions}</td>
                      <td className="tabular-nums">{dvr.reported_mbps} Mbps</td>
                      <td className={dvr.sessions_on_backup ? "tabular-nums text-state-warn" : "tabular-nums"}>
                        {dvr.sessions_on_backup}
                      </td>
                      <td className="tabular-nums text-ink-400">{dvr.authorised_sessions}</td>
                      <td
                        className={
                          Math.abs(dvr.discrepancy) > 2 ? "tabular-nums text-state-warn" : "tabular-nums text-ink-400"
                        }
                        title="Sessions the DVR authorised minus sessions the Outposts report pulling. A persistent gap means one of the two is wrong."
                      >
                        {dvr.discrepancy > 0 ? `+${dvr.discrepancy}` : dvr.discrepancy}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <div className="mt-6">
        <Card title="Nightly auth probe" padded={false}>
          <p className="px-4 pt-4 text-xs text-ink-400">
            Green means the DVR <strong>refused</strong> an unauthenticated pull. A DVR that serves one has an
            open path that bypasses entitlement enforcement, and therefore bypasses billing.
          </p>
          {!data.auth_probe.length ? (
            <Empty>The probe has not run yet.</Empty>
          ) : (
            <div className="overflow-x-auto">
              <table className="table mt-2">
                <thead>
                  <tr>
                    <th>DVR</th>
                    <th>Result</th>
                    <th>Stream probed</th>
                    <th>Tokenless</th>
                    <th>Bad token</th>
                    <th>Last run</th>
                  </tr>
                </thead>
                <tbody>
                  {data.auth_probe.map((probe) => (
                    <tr key={probe.dvr_host}>
                      <td className="font-mono text-xs">{probe.dvr_host}</td>
                      <td>
                        <StatusDot
                          tone={probe.passed ? "ok" : "crit"}
                          label={probe.passed ? "rejected as expected" : "SERVED — auth not enforcing"}
                        />
                      </td>
                      <td className="font-mono text-xs text-ink-400">{probe.stream_name ?? "—"}</td>
                      <td className="tabular-nums">{probe.tokenless_status ?? "no response"}</td>
                      <td className="tabular-nums">{probe.bad_token_status ?? "no response"}</td>
                      <td className="text-ink-400">{formatTime(probe.at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {Object.keys(data.denies_2h).length > 0 && (
        <div className="mt-6">
          <Card title="Denied authorizations by reason (last 2 hours)">
            <ul className="grid gap-2 md:grid-cols-3">
              {Object.entries(data.denies_2h).map(([reason, count]) => (
                <li key={reason} className="flex items-baseline justify-between rounded border border-ink-700 px-3 py-2">
                  <span className="font-mono text-xs text-ink-300">{reason}</span>
                  <span className="tabular-nums">{count}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      )}
    </>
  );
}

function LegendSwatch({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className="h-2.5 w-2.5 rounded-sm" style={{ background: color }} aria-hidden />
      {label}
    </span>
  );
}

